/**
 * Email Templates Management Page
 * Create, edit, and manage email templates with drag-drop editor
 * Now with full WYSIWYG email builder (like Sendy)
 *
 * Created: January 3, 2026
 * Updated: January 4, 2026 - Added rich email builder
 */

import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Badge, Card, Tabs, Tab, Button, Form } from "react-bootstrap";
import Modal from "react-modal";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import CommonStructured from "../../../components/common-components/CommonStructured";
import CmnFilters from "../../../components/lead-management/common/CmnFilters";
import ModalHeadAndClose from "../../../components/payouts-wtf/common/ModalHeadAndClose";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../components/payment-link-system/PaymentLinkTable";
import EmailBuilder from "../../../components/email-broadcast/EmailBuilder";

import {
  fetchEmailTemplatesData,
} from "../../../redux/email-broadcast/emailBroadcastSlice";
import {
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  duplicateEmailTemplate,
  previewEmail,
} from "../../../redux/email-broadcast/emailBroadcastApi";
import { formatDateTimezon } from "../../../components/common-data/Converts";

// Variable Insert Dropdown
const VariableDropdown = ({ onInsert }) => {
  const [isOpen, setIsOpen] = useState(false);
  const variables = [
    { key: "[Name]", description: "Subscriber name" },
    { key: "[Name,fallback=Hey]", description: "Name with fallback" },
    { key: "[Email]", description: "Subscriber email" },
    { key: "[Phone]", description: "Subscriber phone" },
    { key: "[Gym_Name]", description: "Gym name" },
    { key: "[Date]", description: "Current date" },
    { key: "[Unsubscribe_Link]", description: "Unsubscribe link" },
    { key: "[Web_Version_Link]", description: "View in browser link" },
  ];

  return (
    <div className="dropdown d-inline-block">
      <button
        className="btn btn-outline-secondary btn-sm dropdown-toggle"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        Insert Variable
      </button>
      {isOpen && (
        <div className="dropdown-menu show" style={{ maxHeight: "250px", overflowY: "auto" }}>
          {variables.map((v) => (
            <button
              key={v.key}
              className="dropdown-item"
              onClick={() => {
                onInsert(v.key);
                setIsOpen(false);
              }}
            >
              <code className="text-primary">{v.key}</code>
              <small className="text-muted d-block">{v.description}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Template Form Component with Full Email Builder
const TemplateForm = ({ template, closeModal, userInfoData, callFunction }) => {
  // Map backend field names to frontend form (template_name -> name, html_body -> html_content)
  const [formData, setFormData] = useState({
    name: template?.template_name || template?.name || "",
    subject: template?.subject || "",
    from_name: template?.from_name || "WTF Gyms",
    from_email: template?.from_email || "noreply@wtfgyms.com",
    reply_to: template?.reply_to || "support@wtfgyms.com",
    html_content: template?.html_body || template?.html_content || "",
    plain_text: template?.plain_text_body || template?.plain_text || "",
    category: template?.category || "general",
    design_json: template?.design_json || null,
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = details, 2 = builder
  const emailBuilderRef = useRef(null);

  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.subject.trim()) {
      toast.error("Name and Subject are required");
      return;
    }
    setStep(2); // Go to email builder
  };

  const handleBuilderSave = async (data) => {
    setLoading(true);
    try {
      // Map frontend fields to backend field names
      const payload = {
        template_name: formData.name,
        subject: formData.subject,
        from_name: formData.from_name,
        from_email: formData.from_email,
        reply_to: formData.reply_to,
        category: formData.category,
        html_body: data.html,
        plain_text_body: formData.plain_text,
        design_json: data.design,
      };

      let response;
      if (template?.uid) {
        response = await updateEmailTemplate(template.uid, payload, userInfoData?.token);
      } else {
        response = await createEmailTemplate(payload, userInfoData?.token);
      }

      if (response?.status) {
        toast.success(template?.uid ? "Template updated" : "Template created");
        callFunction();
        closeModal();
      } else {
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Template Details Form
  if (step === 1) {
    return (
      <div className="p-3">
        <form onSubmit={handleDetailsSubmit}>
          {/* Basic Info */}
          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Template Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Welcome Email"
                required
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="general">General</option>
                <option value="promotional">Promotional</option>
                <option value="transactional">Transactional</option>
                <option value="notification">Notification</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </Col>
          </Row>

          {/* Subject Line */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Subject Line *</label>
              <VariableDropdown onInsert={(v) => setFormData({ ...formData, subject: formData.subject + v })} />
            </div>
            <input
              type="text"
              className="form-control"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Hi [Name], Check out our latest offers!"
              required
            />
            <small className="text-muted">Use personalization tags like [Name] for dynamic content</small>
          </div>

          {/* From Details */}
          <Row className="mb-3">
            <Col md={4}>
              <label className="form-label">From Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.from_name}
                onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                placeholder="WTF Gyms"
              />
            </Col>
            <Col md={4}>
              <label className="form-label">From Email</label>
              <input
                type="email"
                className="form-control"
                value={formData.from_email}
                onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                placeholder="noreply@wtfgyms.com"
              />
            </Col>
            <Col md={4}>
              <label className="form-label">Reply-To</label>
              <input
                type="email"
                className="form-control"
                value={formData.reply_to}
                onChange={(e) => setFormData({ ...formData, reply_to: e.target.value })}
                placeholder="support@wtfgyms.com"
              />
            </Col>
          </Row>

          {/* Actions */}
          <div className="d-flex gap-2 mt-4">
            <button
              type="submit"
              className="btn btn-primary flex-grow-1"
              style={{ backgroundColor: "#D2000B", borderColor: "#D2000B" }}
            >
              Next: Design Email
            </button>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Step 2: Email Builder
  return (
    <div style={{ height: "calc(100vh - 150px)", display: "flex", flexDirection: "column" }}>
      {/* Step indicator */}
      <div className="bg-light border-bottom p-2 d-flex justify-content-between align-items-center">
        <div>
          <button
            className="btn btn-link text-decoration-none"
            onClick={() => setStep(1)}
          >
            &larr; Back to Details
          </button>
          <span className="ms-2 fw-medium">{formData.name}</span>
          <Badge bg="secondary" className="ms-2">{formData.category}</Badge>
        </div>
        <div className="text-muted small">
          Subject: {formData.subject}
        </div>
      </div>

      {/* Email Builder */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <EmailBuilder
          ref={emailBuilderRef}
          initialDesign={formData.design_json}
          initialHtml={formData.html_content}
          templateName={formData.name}
          templateSubject={formData.subject}
          fromName={formData.from_name}
          fromEmail={formData.from_email}
          token={userInfoData?.token}
          userEmail={userInfoData?.email}
          onSave={handleBuilderSave}
        />
      </div>

      {loading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(255,255,255,0.8)", zIndex: 9999 }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Saving...</span>
            </div>
            <div className="mt-2">Saving template...</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Default HTML template
function getDefaultTemplate() {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #D2000B; color: white; padding: 20px; text-align: center; }
    .header img { max-width: 150px; }
    .content { padding: 30px 20px; background: #ffffff; }
    .footer { background: #f5f5f5; padding: 20px; text-align: center; font-size: 12px; color: #666; }
    .button { display: inline-block; padding: 12px 30px; background: #D2000B; color: white; text-decoration: none; border-radius: 5px; margin: 20px 0; }
    .button:hover { background: #b3000a; }
    @media only screen and (max-width: 600px) {
      .container { width: 100% !important; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <img src="https://wtfgyms.com/logo-white.png" alt="WTF Gyms">
    </div>
    <div class="content">
      <h2>Hi [Name,fallback=there]!</h2>
      <p>Your email content goes here.</p>
      <p>We're excited to share this with you.</p>
      <a href="https://wtfgyms.com" class="button">Learn More</a>
    </div>
    <div class="footer">
      <p>&copy; 2026 WTF Gyms. All rights reserved.</p>
      <p>
        <a href="[Unsubscribe_Link]">Unsubscribe</a> |
        <a href="[Web_Version_Link]">View in Browser</a>
      </p>
    </div>
  </div>
</body>
</html>`;
}

const EmailTemplates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfoData = useSelector(selectUser);
  const { emailTemplates, emailTemplatesLoading } = useSelector(
    (state) => state.emailBroadcast || {}
  );

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);

  const iniAllFilter = { searchString: "", category: "" };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const [filters, setFilters] = useState({ page: 1, limit: 20 });
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const iniModValues = { isModOpen: false, modName: "", rowData: null };
  const [modValues, setModValues] = useState(iniModValues);

  const fetchTemplates = (filters) => {
    dispatch(fetchEmailTemplatesData({ filters, token: userInfoData?.token }));
  };

  useEffect(() => {
    fetchTemplates(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: isPage + 1, limit: pageLimit }));
  }, [isPage, pageLimit]);

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prev) => ({ ...prev, search: allFilters.searchString }));
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setFilters({ page: 1, limit: 20 });
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;

    try {
      const response = await deleteEmailTemplate(template.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Template deleted");
        fetchTemplates(filters);
      } else {
        toast.error(response?.message || "Delete failed");
      }
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await duplicateEmailTemplate(template.uid, userInfoData?.token);
      if (response?.status) {
        toast.success("Template duplicated");
        fetchTemplates(filters);
      } else {
        toast.error(response?.message || "Duplicate failed");
      }
    } catch (error) {
      toast.error("Duplicate failed");
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      general: "secondary",
      promotional: "danger",
      transactional: "success",
      notification: "info",
      newsletter: "primary",
    };
    return <Badge bg={colors[category] || "secondary"}>{category}</Badge>;
  };

  const tbColumns = [
    {
      dataField: "name",
      text: "Template",
      formatter: (_, row) => (
        <div>
          <span className="fw-medium">{row.name}</span>
          <small className="d-block text-muted">{row.subject}</small>
        </div>
      ),
    },
    {
      dataField: "category",
      text: "Category",
      formatter: (_, row) => getCategoryBadge(row.category),
    },
    {
      dataField: "from_name",
      text: "From",
      formatter: (_, row) => (
        <small>
          {row.from_name}<br />
          <span className="text-muted">{row.from_email}</span>
        </small>
      ),
    },
    {
      dataField: "usage_count",
      text: "Used",
      formatter: (_, row) => `${row.usage_count || 0} campaigns`,
    },
    {
      dataField: "date_added",
      text: "Created",
      formatter: (_, row) => (
        <small>{formatDateTimezon(row.date_added) || "N/A"}</small>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              setModValues({ isModOpen: true, modName: "edit", rowData: row });
            }}
          >
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicate(row);
            }}
          >
            Copy
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <HelmetWrapper title="WTF: Email Templates">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        <Modal
          isOpen={modValues.isModOpen}
          onRequestClose={() => setModValues(iniModValues)}
          contentLabel="Template Modal"
          ariaHideApp={false}
          style={{
            content: {
              top: "10px",
              left: "10px",
              right: "10px",
              bottom: "10px",
              maxWidth: "none",
              margin: "0",
              padding: "0",
              overflow: "hidden",
            },
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              zIndex: 1050,
            },
          }}
        >
          <div className="createNewCTOModalContainer">
            <ModalHeadAndClose
              title={modValues.rowData ? "Edit Template" : "Create Template"}
              onClose={() => setModValues(iniModValues)}
            />
            <TemplateForm
              template={modValues.rowData}
              closeModal={() => setModValues(iniModValues)}
              userInfoData={userInfoData}
              callFunction={() => fetchTemplates(filters)}
            />
          </div>
        </Modal>

        {/* Header */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Email Templates</h5>
                <small className="text-muted">
                  {emailTemplates?.pagination?.totalRecords || 0} templates
                </small>
              </div>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: "#D2000B", borderColor: "#D2000B" }}
                onClick={() => setModValues({ isModOpen: true, modName: "create", rowData: null })}
              >
                + Create Template
              </button>
            </div>
          </Col>
        </Row>

        {/* Quick Templates */}
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body>
                <h6 className="mb-3">Quick Start Templates</h6>
                <div className="d-flex gap-2 flex-wrap">
                  {[
                    { name: "Welcome Email", icon: "👋" },
                    { name: "Promotional Offer", icon: "🎁" },
                    { name: "Renewal Reminder", icon: "🔄" },
                    { name: "Newsletter", icon: "📰" },
                    { name: "Birthday Wishes", icon: "🎂" },
                  ].map((t) => (
                    <button
                      key={t.name}
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setModValues({
                        isModOpen: true,
                        modName: "create",
                        rowData: { name: t.name, html_content: getDefaultTemplate() },
                      })}
                    >
                      {t.icon} {t.name}
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <CmnFilters
              from="email-templates"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={emailTemplatesLoading}
              searchPlaceHolder="Search templates..."
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt15">
              {emailTemplatesLoading ? (
                <ReactVortoxSpinner />
              ) : (
                <PaymentLinkTable
                  tableData={emailTemplates?.data}
                  tableColumns={tbColumns}
                  tablePagination={emailTemplates?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default EmailTemplates;
